import { createSlice } from "@reduxjs/toolkit";

const LOCAL_STORAGE_KEY = "preferred_language";

// Helper function to normalize language code
// Converts backend format ("spanish", "english") to frontend format ("es", "en")
const normalizeLanguage = (lang) => {
  if (!lang) return null;
  const normalized = lang.toLowerCase();
  if (normalized === "spanish" || normalized === "es" || normalized.startsWith("es-")) {
    return "es";
  }
  if (normalized === "english" || normalized === "en" || normalized.startsWith("en-")) {
    return "en";
  }
  return null;
};

// Helper function to detect browser language
// Priority: navigator.language (primary) > navigator.languages array (fallback)
const getBrowserLanguage = () => {
  if (typeof window === "undefined") return "en";
  const browserLang = navigator.language || navigator.userLanguage;
  const allLanguages = navigator.languages || [browserLang];
  
  console.log("🌐 LanguageSlice: Browser Language Detection:", {
    "navigator.language": navigator.language,
    "navigator.userLanguage": navigator.userLanguage,
    "navigator.languages": navigator.languages,
    "primary detected": browserLang
  });
  
  // Priority 1: Check primary browser language (navigator.language)
  // This reflects the user's actual primary language preference
  if (browserLang) {
    const primaryLang = browserLang.toLowerCase();
    if (primaryLang.startsWith("es")) {
      console.log("   ✅ Primary language is Spanish");
      return "es";
    }
    if (primaryLang.startsWith("en")) {
      console.log("   ✅ Primary language is English");
      return "en";
    }
  }
  
  // Priority 2: Fallback to navigator.languages array only if primary doesn't match
  // This is for edge cases where primary language is not en/es
  for (const lang of allLanguages) {
    if (lang && lang.toLowerCase().startsWith("es")) {
      console.log("   ⚠️ Spanish found in languages array (fallback)");
      return "es";
    }
    if (lang && lang.toLowerCase().startsWith("en")) {
      console.log("   ⚠️ English found in languages array (fallback)");
      return "en";
    }
  }
  
  console.log("   ⚠️ No matching language found, defaulting to English");
  return "en";
};

const storedLanguageRaw = typeof window !== "undefined" ? localStorage.getItem(LOCAL_STORAGE_KEY) : null;
const normalizedStoredLanguage = normalizeLanguage(storedLanguageRaw);
const browserDetectedLang = getBrowserLanguage();

console.log("📦 LanguageSlice Initialization:", {
  "localStorage (raw)": storedLanguageRaw,
  "localStorage (normalized)": normalizedStoredLanguage,
  "browser detected": browserDetectedLang,
  "final initialLanguage": normalizedStoredLanguage || browserDetectedLang
});

// If we found a stored language, normalize it and save back if it was in wrong format
if (normalizedStoredLanguage && storedLanguageRaw && normalizedStoredLanguage !== storedLanguageRaw.toLowerCase()) {
  if (typeof window !== "undefined") {
    console.log("🔄 Normalizing language in localStorage:", storedLanguageRaw, "→", normalizedStoredLanguage);
    localStorage.setItem(LOCAL_STORAGE_KEY, normalizedStoredLanguage);
  }
}

// If browser detected Spanish and localStorage is empty or has "en", use browser language and save it
let initialLanguage;
if (browserDetectedLang === "es" && (!normalizedStoredLanguage || normalizedStoredLanguage === "en")) {
  initialLanguage = "es";
  if (typeof window !== "undefined") {
    localStorage.setItem(LOCAL_STORAGE_KEY, "es");
    console.log("💾 Auto-saving browser language 'es' to localStorage (from LanguageSlice)");
  }
} else {
  initialLanguage = normalizedStoredLanguage || browserDetectedLang;
}

const languageSlice = createSlice({
  name: "language",
  initialState: {
    current: initialLanguage,
  },
  reducers: {
    setLanguage: (state, action) => {
      const nextLanguage = action.payload || "en";
      console.log("🔄 LanguageSlice: Setting language", {
        "previous": state.current,
        "new": nextLanguage,
        "payload": action.payload
      });
      state.current = nextLanguage;
      if (typeof window !== "undefined") {
        localStorage.setItem(LOCAL_STORAGE_KEY, nextLanguage);
        console.log("💾 Saved to localStorage:", nextLanguage);
      }
    },
  },
});

export const { setLanguage } = languageSlice.actions;
export default languageSlice.reducer;

